import { EditOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { Union } from 'ts-toolbelt';

import { AstroUserProfile } from '@/generated/gql/graphql';
import { NULLISH_VALUE } from '~/common/constants';

import { UserProfileCropDialog } from './UserProfileCropDialog/UserProfileCropDialog';
import { MAX_DIMENSION_PX } from './UserProfileCropDialog/UserProfileCropDialog.util';
import { EditBox, FalconImageStyled } from './UserProfileSummary.styled';

interface UserProfileSummaryProps {
  onPictureEdit: (picture: File) => void;
  picture?: File;
  userProfile?: AstroUserProfile;
  isEditOpen?: boolean;
}

export const UserProfileSummary = ({ picture, userProfile, isEditOpen, onPictureEdit }: UserProfileSummaryProps) => {
  const [isCropOpen, setCropOpen] = useState<boolean>(false);
  const userProfilePicureId = userProfile?.pictureId;
  const prevBlobSrcRef = useRef<string>(undefined);

  const handlePictureCropped = (picture: Union.Nullable<Blob>) => {
    onPictureEdit(picture as File);
  };

  const onDrop: NonNullable<DropzoneOptions['onDrop']> = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      onPictureEdit(file);
      setCropOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { open, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': ['.jpg'], 'image/png': ['.png'] },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
  });

  const pictureBlobSrc = useMemo(() => {
    if (picture) {
      return URL.createObjectURL(picture);
    }
    return undefined;
  }, [picture]);

  useEffect(() => {
    if (prevBlobSrcRef.current) {
      URL.revokeObjectURL(prevBlobSrcRef.current);
    }
    prevBlobSrcRef.current = pictureBlobSrc;
  }, [pictureBlobSrc]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: MAX_DIMENSION_PX,
            height: MAX_DIMENSION_PX,
          }}
        >
          {userProfilePicureId && !pictureBlobSrc ? (
            <FalconImageStyled width={MAX_DIMENSION_PX} height={MAX_DIMENSION_PX} imageId={userProfilePicureId} />
          ) : (
            pictureBlobSrc && !isCropOpen && <img src={pictureBlobSrc} alt="profile" />
          )}
          {isEditOpen && (
            <EditBox onClick={open}>
              <input {...getInputProps()} />
              <EditOutlined fontSize="small" />
            </EditBox>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h5">
            {userProfile?.firstName} {userProfile?.lastName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {userProfile?.jobTitle ?? NULLISH_VALUE}
          </Typography>
        </Box>
        {pictureBlobSrc && (
          <UserProfileCropDialog
            open={isCropOpen}
            setOpen={setCropOpen}
            pictureBlobSrc={pictureBlobSrc}
            onPictureCropped={handlePictureCropped}
          />
        )}
      </Box>
    </>
  );
};
